import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Loader2,
  MessageSquare,
  Paperclip,
  RefreshCw,
  Video,
  Wand2,
  XCircle,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import knowledgeGapsService, {
  type KGSummaryResponse,
  type KGRawFindingResponse,
} from '@/lib/api/knowledgeGaps';
import { LOW_CONFIDENCE_THRESHOLD } from './constants';
import { getCategoryMeta, getFrequencyMeta, getResolutionMeta, formatDate } from './helpers';
import { ResolvedBanner } from './ResolvedBanner';
import { AttachKnowledgeSourceModal } from './attach-source/AttachKnowledgeSourceModal';
import { ATTACHABLE_CATEGORIES } from './attach-source/constants';
import { LaunchCaptureModal } from './capture-demo/LaunchCaptureModal';
import { CapturePreviewModal } from './capture-demo/CapturePreviewModal';
import { patchKnowledgeGapSummaryCache } from './queryCache';

export function GapDetail({
  orgId,
  summary,
  onResolveManual,
  onDismiss,
  onResolveViaPrompt,
  onAnswerInTraining,
  isResolvingManual,
  isDismissing,
  isStartingPromptUpdate,
  isStartingTraining,
  slowNotice,
}: {
  orgId: string;
  summary: KGSummaryResponse;
  onResolveManual: () => void;
  onDismiss: () => void;
  onResolveViaPrompt: () => void;
  onAnswerInTraining: () => void;
  isResolvingManual: boolean;
  isDismissing: boolean;
  isStartingPromptUpdate: boolean;
  isStartingTraining: boolean;
  slowNotice: boolean;
}) {
  const queryClient = useQueryClient();
  const meta = getCategoryMeta(summary.category);
  const isMutating =
    isResolvingManual || isDismissing || isStartingPromptUpdate || isStartingTraining;

  const isInProgress =
    summary.status === 'resolving_via_prompt' ||
    summary.in_progress_prompt_update !== null;
  const isResolved = summary.status === 'resolved';
  const isDismissed = summary.status === 'dismissed';
  const resolutionMeta = getResolutionMeta(summary.resolution_type);
  const isPromptEligible =
    summary.status === 'active' && summary.resolution_type === 'prompt_update';
  const canAttachSource =
    summary.status === 'active' &&
    (summary.resolution_type === 'other' ||
      summary.resolution_type === 'product_capture') &&
    (ATTACHABLE_CATEGORIES as ReadonlyArray<string>).includes(summary.category);
  // Eligibility for the training-session resolution path. Mirrors the
  // backend gate in GapTrainingService (defense-in-depth — server still
  // re-validates and 422s on mismatch).
  const canTrainAnswer =
    summary.status === 'active' &&
    summary.category === 'unanswered_question' &&
    summary.resolution_type === 'other';
  const lowConfidence =
    summary.resolution_type === 'prompt_update' &&
    summary.resolution_type_confidence !== null &&
    summary.resolution_type_confidence < LOW_CONFIDENCE_THRESHOLD;
  const isConfigChange =
    summary.status === 'active' && summary.resolution_type === 'config_change';
  const inFlightCapture = summary.in_flight_capture;
  const isCapturing =
    summary.status === 'resolving_via_product_capture' || inFlightCapture !== null;
  const canCaptureDemo =
    summary.status === 'active' && summary.resolution_type === 'product_capture';
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [dismissConfirmOpen, setDismissConfirmOpen] = useState(false);
  const [launchCaptureOpen, setLaunchCaptureOpen] = useState(false);
  const [capturePreviewOpen, setCapturePreviewOpen] = useState(false);
  const frequency = getFrequencyMeta(summary.occurrence_count);

  // Discard mutation for the in-flight capture cards. Centralised here so
  // both the "in-progress" and "ready" cards share invalidation + toast
  // wiring.
  const discardCapture = useMutation({
    mutationFn: async () => {
      const response = await knowledgeGapsService.cancelInFlightCapture(
        orgId,
        summary.id,
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to discard capture.');
      }
      return response.data;
    },
    onSuccess: (updated) => {
      patchKnowledgeGapSummaryCache(queryClient, orgId, updated);
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-summaries', orgId] });
      queryClient.invalidateQueries({
        queryKey: ['knowledge-gap-summary-detail', orgId, summary.id],
      });
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-report', orgId] });
      toast({
        title: 'Capture discarded',
        description: 'You can launch a new capture for this gap.',
      });
    },
    onError: () => {
      toast({
        title: 'Discard failed',
        description: 'We could not discard this capture. Try again.',
        variant: 'destructive',
      });
    },
  });

  const { data: findings, isLoading: findingsLoading } = useQuery({
    queryKey: ['knowledge-gaps-findings', orgId, summary.id],
    queryFn: async () => {
      const res = await knowledgeGapsService.getSummaryFindings(orgId, summary.id);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load evidence');
      }
      return res.data;
    },
    enabled: !!summary.id,
  });

  const confidenceLabel =
    summary.resolution_type_confidence !== null
      ? `${Math.round(summary.resolution_type_confidence * 100)}%`
      : '—';

  // State 5 signal: a prior attempt ended in no_changes or failed. Only
  // surfaced when the gap is active+prompt-eligible (nothing to retry
  // otherwise). Cleared by the endpoint on a new attempt start and by the
  // resolution hook on success.
  const lastOutcome =
    isPromptEligible && !isInProgress
      ? summary.last_prompt_update_outcome
      : null;

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b card-border px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{summary.topic}</h2>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {isInProgress ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Optimizing prompt…</span>
              </div>
            ) : isCapturing && inFlightCapture ? (
              <CaptureStateActions
                inFlight={inFlightCapture}
                summaryId={summary.id}
                onOpenPreview={() => setCapturePreviewOpen(true)}
                onDiscard={() => discardCapture.mutate()}
                isDiscarding={discardCapture.isPending}
              />
            ) : isCapturing ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Capture in progress…</span>
              </div>
            ) : isResolved || isDismissed ? null : (
              <>
                {isPromptEligible && (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={isMutating}
                    onClick={onResolveViaPrompt}
                    title={
                      lowConfidence
                        ? 'Low confidence classification — review before applying'
                        : undefined
                    }
                  >
                    {isStartingPromptUpdate ? (
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : lastOutcome ? (
                      <RefreshCw className="h-4 w-4 mr-1.5" />
                    ) : (
                      <Wand2 className="h-4 w-4 mr-1.5" />
                    )}
                    {lastOutcome ? 'Try again' : 'Resolve via prompt update'}
                    {lowConfidence && (
                      <span className="ml-1.5 text-xs opacity-75">(?)</span>
                    )}
                  </Button>
                )}
                {canAttachSource && (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={isMutating}
                    onClick={() => setAttachModalOpen(true)}
                  >
                    <Paperclip className="h-4 w-4 mr-1.5" />
                    Attach knowledge source
                  </Button>
                )}
                {canCaptureDemo && (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={isMutating}
                    onClick={() => setLaunchCaptureOpen(true)}
                  >
                    <Video className="h-4 w-4 mr-1.5" />
                    Capture demo
                  </Button>
                )}
                {canTrainAnswer && (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={isMutating}
                    onClick={onAnswerInTraining}
                  >
                    {isStartingTraining ? (
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                      <MessageSquare className="h-4 w-4 mr-1.5" />
                    )}
                    Answer in training
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isMutating}
                  onClick={onResolveManual}
                >
                  {isResolvingManual ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-1.5" />
                  )}
                  Mark resolved manually
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isMutating}
                  onClick={() => setDismissConfirmOpen(true)}
                >
                  {isDismissing ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-1.5" />
                  )}
                  Dismiss
                </Button>
              </>
            )}
          </div>
        </div>
        {isInProgress && (
          <p className="text-xs text-muted-foreground mt-2">
            Usually takes 30–90 seconds.
            {slowNotice && (
              <span className="ml-1 text-amber-600 dark:text-amber-400">
                This is taking longer than usual.
              </span>
            )}
          </p>
        )}
        {lastOutcome?.status === 'no_changes' && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span title={summary.suggested_action ?? undefined}>
              No prompt change suggested on the last attempt.
            </span>
          </div>
        )}
        {lastOutcome?.status === 'failed' && (
          <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 mt-2">
            <XCircle className="h-3.5 w-3.5 shrink-0" />
            <span title={lastOutcome.error ?? undefined}>
              Optimization failed on the last attempt.
            </span>
          </div>
        )}
        {isConfigChange && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>
              This gap requires updating the agent's configuration. Make the
              change in your agent settings, then mark this gap resolved.
            </span>
          </div>
        )}
      </div>

      <AlertDialog open={dismissConfirmOpen} onOpenChange={setDismissConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss this gap?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the gap from the open queue. It will not add knowledge,
              update the agent, or mark the issue as resolved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDismissing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDismissing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={onDismiss}
            >
              {isDismissing ? 'Dismissing...' : 'Dismiss gap'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resolved banner (States 3 / 4) — rendered between header and scrollable content */}
      {isResolved && <ResolvedBanner orgId={orgId} summary={summary} />}

      <div className="flex-1 overflow-auto px-6 py-5 space-y-6">
        {/* Attributes */}
        <section>
          <h3 className="text-sm font-semibold border-b border-border pb-2 mb-3">
            Attributes
          </h3>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <span className="text-muted-foreground">Category</span>
            <span>{meta.label}</span>
            <span className="text-muted-foreground">Frequency</span>
            <span className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={frequency.className}>
                {frequency.label}
              </Badge>
              <span className="text-xs text-muted-foreground">{frequency.detail}</span>
            </span>
            <span className="text-muted-foreground">First Seen</span>
            <span>{formatDate(summary.first_seen_at)}</span>
            <span className="text-muted-foreground">Last Seen</span>
            <span>{formatDate(summary.last_seen_at)}</span>
            <span className="text-muted-foreground">Resolution</span>
            <span>
              {resolutionMeta.label}
              {summary.resolution_type_confidence !== null && (
                <span className="ml-2 text-xs text-muted-foreground">
                  ({confidenceLabel})
                </span>
              )}
            </span>
          </div>
        </section>

        {/* Gap Explanation */}
        <section>
          <h3 className="text-sm font-semibold border-b border-border pb-2 mb-3">
            Gap Explanation
          </h3>
          <p className="text-sm">{summary.summary}</p>
        </section>

        {/* Evidence */}
        <section>
          <div className="mb-3 flex items-center justify-between gap-3 border-b border-border pb-2">
            <h3 className="text-sm font-semibold">Conversation evidence</h3>
            <Badge variant="outline" className="text-xs font-normal">
              {findingsLoading ? 'Loading' : `${findings?.length ?? 0} snippet${findings?.length === 1 ? '' : 's'}`}
            </Badge>
          </div>
          {findingsLoading && (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ))}
            </div>
          )}
          {!findingsLoading && findings && findings.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No raw evidence available.
            </p>
          )}
          {findings?.map((finding: KGRawFindingResponse, idx: number) => (
            <div
              key={finding.id}
              className="rounded-md border border-border bg-muted/30 p-4 space-y-2 mb-3 last:mb-0"
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <span>Evidence #{idx + 1}</span>
                {finding.session_id ? (
                  <Link
                    to={`/analytics/sessions?sessionId=${finding.session_id}`}
                    className="text-xs text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    View session →
                  </Link>
                ) : finding.provenance_type === 'simulation' || finding.source_type === 'simulated' ? (
                  finding.eval_batch_id ? (
                    <Link
                      to={`/evaluation/simulations?batch=${finding.eval_batch_id}`}
                      className="text-xs text-muted-foreground transition-colors hover:text-blue-600 dark:hover:text-blue-400"
                      title={finding.eval_dataset_name ?? finding.eval_batch_name ?? undefined}
                    >
                      Simulation →
                    </Link>
                  ) : (
                    <Badge variant="outline" className="text-xs font-normal">
                      Simulation
                    </Badge>
                  )
                ) : (
                  <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                    Trace only
                  </Badge>
                )}
              </div>
              {finding.finding_data.evidence && (
                <p className="text-sm">{finding.finding_data.evidence}</p>
              )}
              {finding.finding_data.details && (
                <p className="text-sm text-muted-foreground">
                  {finding.finding_data.details}
                </p>
              )}
            </div>
          ))}
        </section>

        {/* Suggested Action */}
        {summary.suggested_action && (
          <section>
            <h3 className="text-sm font-semibold border-b border-border pb-2 mb-3">
              Suggested Action
            </h3>
            <p className="text-sm">{summary.suggested_action}</p>
          </section>
        )}
      </div>

      {canAttachSource && (
        <AttachKnowledgeSourceModal
          open={attachModalOpen}
          onOpenChange={setAttachModalOpen}
          orgId={orgId}
          summary={summary}
          onResolved={() => {
            // The mutation already invalidates the relevant queries; the
            // outer ActiveGapsTab will re-fetch and re-render the resolved
            // state on its own.
          }}
        />
      )}

      {canCaptureDemo && (
        <LaunchCaptureModal
          open={launchCaptureOpen}
          onOpenChange={setLaunchCaptureOpen}
          orgId={orgId}
          summary={summary}
        />
      )}

      {isCapturing && (
        <CapturePreviewModal
          open={capturePreviewOpen}
          onOpenChange={setCapturePreviewOpen}
          orgId={orgId}
          summary={summary}
          onResolved={() => {
            // Same fall-through pattern as AttachKnowledgeSourceModal — the
            // mutation already invalidates queries; the outer ActiveGapsTab
            // re-fetches and re-renders.
          }}
          onDiscard={() => {
            // After discard, surface the LaunchCaptureModal so the user can
            // immediately kick off a new capture if they want.
            setLaunchCaptureOpen(true);
          }}
        />
      )}
    </div>
  );
}

/**
 * Header sub-component that renders the action buttons for an in-flight
 * product-capture session: open studio (live link), view captured demo
 * (when complete + ready), and discard.
 */
function CaptureStateActions({
  inFlight,
  summaryId,
  onOpenPreview,
  onDiscard,
  isDiscarding,
}: {
  inFlight: NonNullable<KGSummaryResponse['in_flight_capture']>;
  summaryId: string;
  onOpenPreview: () => void;
  onDiscard: () => void;
  isDiscarding: boolean;
}) {
  const navigate = useNavigate();
  const isLive =
    inFlight.status === 'launching' || inFlight.status === 'active';
  // ``status === 'complete'`` only means the Demo Studio finalize step
  // muxed the video. Segmentation runs against the resulting
  // OrganizationVideo afterward, so the asset isn't actually applicable
  // until ``video_ready`` flips true. Treat that interval as a continuation
  // of "Processing capture…" rather than letting the card render with only
  // a Discard button.
  const isProcessing =
    inFlight.status === 'processing' ||
    (inFlight.status === 'complete' && !inFlight.video_ready);
  const isReady = inFlight.status === 'complete' && inFlight.video_ready;
  const studioHref = `/demo-studio/sessions/${inFlight.session_id}?source=knowledge_gap&summary_id=${summaryId}`;

  return (
    <div className="flex items-center gap-2 shrink-0">
      {isLive && (
        <>
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Capture in progress…
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={isDiscarding}
            onClick={() => navigate(studioHref)}
          >
            <ExternalLink className="h-4 w-4 mr-1.5" />
            Open studio
          </Button>
        </>
      )}
      {isProcessing && (
        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Processing capture…
        </span>
      )}
      {isReady && (
        <Button variant="default" size="sm" onClick={onOpenPreview} disabled={isDiscarding}>
          <Video className="h-4 w-4 mr-1.5" />
          View capture
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={onDiscard}
        disabled={isDiscarding}
      >
        {isDiscarding ? (
          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
        ) : (
          <XCircle className="h-4 w-4 mr-1.5" />
        )}
        Discard
      </Button>
    </div>
  );
}
